import React from 'react';

export type UseManifestLoadingProgressResult = {
  loadingProgress: number | null;
  onXhrSetup: (xhr: XMLHttpRequest, url: string) => void;
  reset: () => void;
};

/**
 * Tracks simulated loading progress for an HLS manifest request.
 *
 * Because the Avigilon server holds the manifest connection open during
 * processing (~13s) before sending a response, there is no real progress
 * to report. Instead, this hook uses an asymptotic simulation — the same
 * pattern used by YouTube and similar apps — that advances quickly at first
 * then slows as it approaches 95%, giving users a responsive sense of
 * progress without overpromising completion. Once the manifest loads, it
 * snaps to 100% and fades out.
 */
export function useManifestLoadingProgress(): UseManifestLoadingProgressResult {
  const [loadingProgress, setLoadingProgress] = React.useState<number | null>(
    null,
  );
  const manifestLoadedRef = React.useRef(false);
  const progressIntervalRef =
    React.useRef<ReturnType<typeof setInterval>>(null);
  const clearProgressTimeoutRef =
    React.useRef<ReturnType<typeof setTimeout>>(null);
  const isMountedRef = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
      if (clearProgressTimeoutRef.current)
        clearTimeout(clearProgressTimeoutRef.current);
    };
  }, []);

  const reset = React.useCallback((): void => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    if (clearProgressTimeoutRef.current)
      clearTimeout(clearProgressTimeoutRef.current);
    setLoadingProgress(null);
    manifestLoadedRef.current = false;
  }, []);

  const onXhrSetup = React.useCallback(
    (xhr: XMLHttpRequest, url: string): void => {
      if (!url.includes('/index') || manifestLoadedRef.current) return;

      // Mark immediately so subsequent xhrSetup calls (sub-playlists,
      // segments) don't start a second interval
      manifestLoadedRef.current = true;

      // Simulate progress since the server holds the connection
      // open during processing (~13s) before sending the response
      let simulatedProgress = 0;
      if (!isMountedRef.current) return;
      setLoadingProgress(0);
      progressIntervalRef.current = setInterval(() => {
        if (!isMountedRef.current) return;
        simulatedProgress += (95 - simulatedProgress) * 0.08;
        setLoadingProgress(Math.round(simulatedProgress));
      }, 500);

      xhr.addEventListener('load', () => {
        if (!isMountedRef.current) return;
        clearInterval(progressIntervalRef.current);
        setLoadingProgress(100);
        clearProgressTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          setLoadingProgress(null);
        }, 1000);
      });
      xhr.addEventListener('error', () => {
        if (!isMountedRef.current) return;
        clearInterval(progressIntervalRef.current);
        setLoadingProgress(null);
        if (clearProgressTimeoutRef.current)
          clearTimeout(clearProgressTimeoutRef.current);
        manifestLoadedRef.current = false;
      });
    },
    [],
  );

  return { loadingProgress, onXhrSetup, reset };
}
